import React, { useState } from 'react'
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { notification, Spin } from 'antd';
import Modal from "../../components/uiComponent/modal/index";
import Stepper from '../../components/uiComponent/newStepper';
import Button from "../../components/uiComponent/button/index";
import StepsData from "./stepsData";
import { extend, isEmpty, trim } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { GetPresignedUrl } from '../../slices/aws-services';
import { setItem } from '../../slices/auth';
import { InitialInventoryUpload as UploadInitialInventoryFile } from '../../slices/location-management';
import { GetAllProductIdentifiers } from '../../slices/3-layer-products';

import icon from "../../assets/icons/close-icon-blue.svg"
import { initialInventorySampleFile, initialInventorySellerWarehouseSampleFile, fillInventoryTemplate } from '../../utils/download-file';
import { AddProductManualModalWrapper } from './style';

// Seeds initial stock (+ mandatory unit cost) for EXISTING products that never went through a PO.
// Warehouse accounts must provide a User Email per row (it picks the client account); Seller + Warehouse
// accounts upload for their own account, so the User Email column is removed entirely (config + sample).
// Blank Location Name -> staging-location (backend).
const InitialInventory = ({ addProduct, setAddProduct, refreshGridClick, onUploaded, embedded = false, prefillSampleWithIdentifiers = false }) => {
  const dispatch = useDispatch()

  const { currentUser } = useSelector((state) => state.auth || {});
  const isSellerPlusWarehouse = currentUser?.role?.id === 5;

  // "Download Sample File" -> instead of the static blank template, generate the initial-inventory
  // template pre-filled with the user's product identifiers (Business Onboarding). With no products it
  // falls back to a clean template (header + instructions, dummy rows stripped).
  const downloadPrefilledSample = async () => {
    setSampleDownloading(true);
    try {
      const { payload } = await dispatch(GetAllProductIdentifiers());
      const identifiers = payload?.success ? payload.result || [] : [];
      await fillInventoryTemplate(identifiers, { sellerWarehouse: isSellerPlusWarehouse });
    } catch (error) {
      notification.error({ message: 'Download Sample', description: 'Could not generate the sample file.', top: 65 });
    } finally {
      setSampleDownloading(false);
    }
  };

  const INITIAL_INVENTORY_OPTIONS = [
    // Seller + Warehouse accounts don't get a User Email column at all
    ...(isSellerPlusWarehouse ? [] : [{ label: 'User Email', value: 'Email', important: true, optional: false }]),
    { label: 'Product Identifier', value: 'Identifier', important: true, optional: false },
    { label: 'Units', value: 'Units', important: true, optional: false },
    { label: 'Location Name', value: 'Location', important: false, optional: true },
    { label: 'Unit Cost', value: 'Unit Cost', important: true, optional: false },
  ];

  // sample download matches the account type: warehouse template has User Email, seller+warehouse doesn't
  const sampleFileLink = isSellerPlusWarehouse ? initialInventorySellerWarehouseSampleFile : initialInventorySampleFile;

  const [currentStep, setCurrentStep] = useState(0);
  const [fileData, setFileData] = useState({ rows: [], columns: [] });
  const [requiredColumns] = useState(INITIAL_INVENTORY_OPTIONS);
  const [dataToProceed, setDataToProceed] = useState([]);
  const [fileColumns, setFileColumns] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [fileRows, setFileRows] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [sampleDownloading, setSampleDownloading] = useState(false);
  const [isError, setIsError] = useState(false)

  const steps = [
    { title: 'Step 1', description: 'Upload File' },
    { title: 'Step 2', description: 'Data Mapping' },
    { title: 'Step 3', description: 'Review Errors' },
  ];

  const hasMissingValues = (fileRows) => {
    if (!fileRows?.length) return true;
    if (
      fileRows.every(row => !row['Product Identifier']) ||
      fileRows.every(row => !row['Units']) ||
      fileRows.every(row => !row['Unit Cost']) ||
      (!isSellerPlusWarehouse && fileRows.every(row => !row['User Email']))
    ) {
      return true;
    }
    else return false
  };

  const handleDisabled = () => {
    if (
      currentStep === 0 &&
      (!fileList?.length)
    ) {
      return true;
    }
    if (currentStep === 1 && hasMissingValues(fileRows)) return true;
    if (
      currentStep === 2 &&
      fileRows.every((row) => row.reason)
    ) {
      return true;
    }
    if (currentStep === 3) {
      return true;
    }

    return false;
  };

  const setFileColumnsToUse = (columns) => {
    const cols = columns.map((col) => ({ label: col, value: col }));
    setFileColumns(cols);
  };

  const parseFileData = () => {
    const parsedRows = [];

    fileRows.forEach(row => {
      const allValues = Object.values(row).map(v => (typeof v === 'string' ? v.trim() : v));
      const isEmptyRow = allValues.every(v => v === '' || v === null || v === undefined);
      if (isEmptyRow) return; // skipping row, if it's completely empty

      const allKeys = Object.keys(row);
      for (const key of allKeys) {
        if (key === 'Units' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
          extend(row, { reason: 'Unit must be a Number' });
        }
        if (key === 'Units' && !row[key]) {
          extend(row, { reason: 'Unit is required' });
        }
        if (key === 'Product Identifier' && !row[key]) {
          extend(row, { reason: 'Product Identifier is required' });
        }
        if (key === 'User Email' && row[key] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row[key])) {
          extend(row, { reason: 'User Email must be a valid email address' });
        }
        if (key === 'User Email' && !row[key] && !isSellerPlusWarehouse) {
          extend(row, { reason: 'User Email is required' });
        }
        if (key === 'Unit Cost' && !row[key]) {
          extend(row, { reason: 'Unit Cost is required' });
        }
        if (key === 'Unit Cost' && row[key] && (isNaN(row[key]) || Number(row[key]) <= 0)) {
          extend(row, { reason: 'Unit Cost must be a positive number' });
        }
      }

      parsedRows.push(row);
    });
    parsedRows.filter((row) => row.reason).length > 0 ? setIsError(true) : setIsError(false);
    setFileRows(parsedRows);
  };

  const convertToXlsx = (fileData) => {
    const parsedData = typeof fileData === 'string' ? Papa.parse(fileData, { header: true }).data : fileData;
    const worksheet = XLSX.utils.json_to_sheet(parsedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const xlsxBinary = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([xlsxBinary], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return blob;
  };

  const handleSubmitFile = async () => {
    let uploadedFile = {};
    if (!isEmpty(fileList[0])) {
      const fileData = fileRows.filter(row => !row.reason);
      const fileType = fileList[0]?.name.split('.').pop().toLowerCase();
      let data = {};
      if (fileType === 'csv') {
        data = Papa.unparse(fileData);
      } else if (fileType === 'xlsx' || fileType === 'xls') {
        data = convertToXlsx(fileData);
      } else {
        notification.error({
          message: 'File Type Error',
          description: 'Unsupported File Type'
        });
        return;
      }

      setFileUploading(true);
      dispatch(setItem({
        field: 'showFileHeader',
        value: false
      }));
      await dispatch(GetPresignedUrl({ files: [fileList[0]], fileData: data })).then(({ payload }) => {
        if (!payload?.error && !payload?.error?.error && payload) {
          uploadedFile = payload[0];
          if (uploadedFile) {
            dispatch(UploadInitialInventoryFile({ fileName: uploadedFile?.key, fileUrl: uploadedFile?.url, fileType: uploadedFile?.extension })).then(() => {
              setFileUploading(false)
              if (onUploaded) onUploaded(); // success hook for external drivers (e.g. Business Onboarding step 3)
            })
          }
        } else {
          setFileUploading(false);
        }
        dispatch(setItem({
          field: 'showFileHeader',
          value: true
        }));
      });
    }
  };

  const clearStates = () => {
    setFileData({ rows: [], columns: [] });
    setFileRows([]);
    setFileColumns([]);
    setFileList([]);
    setCurrentStep(0);
    setAddProduct(false);
    refreshGridClick();
  };

  const nextStep = async () => {
    if (currentStep === 0 && isEmpty(dataToProceed)) {
      const { rows = [], columns = [] } = fileData;
      if (rows?.length) {
        const matchedCols = [];
        const labels = Object.keys(rows[0]);
        const mappedData = [];
        requiredColumns?.forEach(({ label, important = false, optional = true }) => {
          const item = labels?.find(option => option === label);
          let dataToPush = { label, data: [], important, optional };

          if (!isEmpty(item)) {
            dataToPush = { label, value: label, data: rows.map(item => trim(item[label]) || ''), important, optional };
            matchedCols.push(item);
          }
          mappedData.push(dataToPush);
        });
        setDataToProceed(mappedData)
        setFileColumnsToUse(columns);
      } else setDataToProceed([]);
    }
    if (currentStep === 1) {
      parseFileData();
    }
    if (currentStep === 2) {
      handleSubmitFile();
    }
    currentStep !== 3 && setCurrentStep(currentStep + 1);
  };
  return (
    <div>
      <Modal
        open={addProduct}
        onClose={() => setAddProduct(false)}
        title={null}
        header={null}
        footer={null}
        className="add-product-flow"
        closeButton={false}
        width={808}
        onHide={null}
        onSave={() => {
          setAddProduct(false)
        }}
      >
        <AddProductManualModalWrapper>
          <div className='header-wrapper'><span
          >Initial Inventory</span><img onClick={() => clearStates()} className='cursor-pointer' src={icon} /></div>
          <Stepper
            currentStep={currentStep}
            steps={steps}
            direction="horizontal"
            size="default"
            status="process"
          />
          <Spin tip={sampleDownloading ? 'Preparing sample file…' : 'Loading..'} spinning={fileUploading || sampleDownloading}>
            <StepsData
              setAddProduct={setAddProduct}
              setDataToProceed={setDataToProceed}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              setFileData={setFileData}
              setFileList={setFileList}
              setFileColumns={setFileColumns}
              setFileRows={setFileRows}
              dataToProceed={dataToProceed}
              fileData={fileData}
              fileRows={fileRows}
              fileList={fileList}
              fileColumns={fileColumns}
              clearStates={clearStates}
              setIsError={setIsError}
              sampleFileLink={prefillSampleWithIdentifiers ? undefined : sampleFileLink}
              downloadSampleTemplate={prefillSampleWithIdentifiers ? downloadPrefilledSample : undefined}
              bulkAddInventory={true}
              embedded={embedded}
            />
          </Spin>
          {currentStep === 3 ? null : <footer>
            <Button
              text={currentStep === 2 ? 'Re-Upload File' : 'Back'}
              className="outlined"
              onClick={() => {
                if (currentStep === 2) {
                  setFileData({ rows: [], columns: [] });
                  setFileRows([]);
                  setFileColumns([]);
                  setFileList([]);
                  setCurrentStep(0);
                }
                else if (currentStep === 0) {
                  setFileList([]);
                  setAddProduct(false)
                }
                else {
                  setCurrentStep(currentStep - 1)
                }
              }}
            />
            <Button
              text={currentStep === 2 && !isError ? 'Proceed' : currentStep === 2 && isError ? 'Discard Errors & Proceed' : 'Next'}
              onClick={() => nextStep()}
              disabled={currentStep === 3 || handleDisabled()}
            />
          </footer>}
        </AddProductManualModalWrapper>
      </Modal>

    </div>
  )
}

export default InitialInventory;
